import { FC } from "react";
import { useTranslation } from "react-i18next";
import { CodeBlock } from "./CodeBlock";
import { useDocsLanguage } from "../context";
import { CodeSamplesProps } from "../interfaces";
import { SYNTAX_BY_CODE_LANGUAGE } from "../utils";

export const CodeSamples: FC<CodeSamplesProps> = (props) => {
  const { samples } = props;
  const { t } = useTranslation();
  // YOSHI-10464: the language is one choice for the whole portal, not one per block. Picking C#
  // here switches every other block of this page and of the rest of the portal, so an integrator
  // never has to make the same choice again on the next endpoint.
  const { codeLanguage, setCodeLanguage } = useDocsLanguage();

  // A block that does not offer the chosen language falls back to its first sample instead of
  // rendering empty: showing what there is beats losing the snippet.
  const activeSample =
    samples.find((sample) => sample.language === codeLanguage) ?? samples[0];

  return (
    <div className="space-y-3">
      <div
        role="tablist"
        aria-label={t("docs.common.codeExamples")}
        className="flex gap-1 overflow-x-auto border-b border-gray-200 pb-px"
      >
        {/* YOSHI-10464: the strip scrolls sideways when the five languages do not fit, and a
            scrolling box clips what sticks out of it on every side, not only sideways. An outset
            focus ring reached past the top and left edges of the first tab and came back cut, so
            the ring is drawn inside the button and every tab carries the underline of the active
            one in transparent, which keeps the five the same height. */}
        {samples.map((sample) => (
          <button
            key={sample.language}
            type="button"
            role="tab"
            aria-selected={sample.language === activeSample.language}
            onClick={() => setCodeLanguage(sample.language)}
            className={`shrink-0 rounded-t-md border-b-2 px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary ${
              sample.language === activeSample.language
                ? "border-primary font-semibold text-primary"
                : "border-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            }`}
          >
            {sample.label}
          </button>
        ))}
      </div>
      <CodeBlock
        code={activeSample.code}
        language={activeSample.label}
        syntax={SYNTAX_BY_CODE_LANGUAGE[activeSample.language]}
      />
    </div>
  );
};
